import { useCallback, useEffect, useRef, useState } from "react";

// Cache mémoire simple (stale-while-revalidate) : on réaffiche instantanément la
// dernière valeur connue, puis on revalide en arrière-plan. Évite les écrans vides
// clignotants et les "aucun résultat" affichés pendant le chargement.
const cache = new Map();

export function useAsyncData(key, fetcher) {
  const [data, setData] = useState(() => (cache.has(key) ? cache.get(key) : null));
  const [loading, setLoading] = useState(() => !cache.has(key));
  const [error, setError] = useState(false);
  const fRef = useRef(fetcher);
  fRef.current = fetcher;

  const load = useCallback(async () => {
    setError(false);
    if (!cache.has(key)) setLoading(true);
    // 3 tentatives avec léger backoff pour absorber les échecs transitoires
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fRef.current();
        cache.set(key, res);
        setData(res);
        setLoading(false);
        setError(false);
        return;
      } catch (e) {
        if (attempt < 2) await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
      }
    }
    setLoading(false);
    if (!cache.has(key)) setError(true);
  }, [key]);

  useEffect(() => { load(); }, [load]);

  const set = useCallback((v) => {
    const next = typeof v === "function" ? v(cache.has(key) ? cache.get(key) : null) : v;
    cache.set(key, next);
    setData(next);
  }, [key]);

  return { data, loading, error, reload: load, set };
}

export const dropCache = (key) => cache.delete(key);
